import { useState, useEffect, useRef } from "react";
import { captureException, captureMessage } from "../lib/errorReporting";
import { supabase } from "../lib/supabase";
import { rowToBet } from "../lib/storage";
import { loadUserBets, BetLoadError, loadFailureReason, isLoadCancelled } from "../lib/bet-load";
import { nextLocalTick } from "../lib/local-tick";
import { authSubject } from "../lib/authSubject";

// Single job: load the signed-in user's bets from Supabase into App state and expose the
// sync state the pages branch on. Two derived booleans (UI critique bug-fix batch item C):
//   syncLoading -- signed in, a fetch can happen, nothing landed yet, nothing failed -> skeleton
//   syncError   -- the fetch failed AND nothing is showing -> error card with Retry
// A failure over an already-populated list (a TOKEN_REFRESHED re-run during a transient
// outage -- CC-IMPL-021: same user.id, new object) sets syncFailed but NOT syncError, so the
// list stays.
//
// #1554 (brief docs/audits/2026-10-17-1554-paged-bet-load-brief.md rev 2):
// - The read is src/lib/bet-load.js: keyset pages of bets with their legs embedded. It returns
//   every bet with every leg or throws; setBets only ever sees a complete set.
// - Load sequence (Ultron G3/G4): every effect run (user change, TOKEN_REFRESHED, sign-out) and
//   every retrySync takes a new number from loadSeqRef; only the newest-started load may commit,
//   report, or set state. A subject change is always an effect run, so the sequence is also the
//   generation token: an A->B->A switch makes A's in-flight load stale.
// - setBets(bets, { loadTick }): the guarded setter (useBetRemoval) keeps a bet addBet added after
//   this load started.
// - Severity (CC-IMPL-037): a first effect failure that the 2s retry can fix is a captureMessage
//   warning; a failure nothing retries (the retry, retrySync, mapper, truncated) is captureException.
//   Tags carry the loader's reason code (bet-load.js LOAD_REASONS).
const RETRY_DELAY_MS = 2000;
const NOT_RETRYABLE = new Set(["mapper", "truncated"]);

// useAuth publishes a new subject (and supabase-js switches the token) before React re-renders, so the
// sequence alone misses that window: a page answered under the new token must not let this load commit
// (#1554 pre-Codex L1, #1461 pattern). undefined = never published (no auth source mounted).
const subjectIs = (userId) => {
  const s = authSubject();
  return s === undefined || s === userId;
};

// One load attempt: every bet with its legs, mapped. Throws a BetLoadError.
async function loadAndMap(userId, isCurrent) {
  const rows = await loadUserBets(supabase, userId, { isCurrent });
  try {
    return rows.map((row) => rowToBet(row, row.bet_legs));
  } catch (err) {
    throw new BetLoadError("mapper", err?.message || "rowToBet failed", { cause: err });
  }
}

export function useCloudSync({ user, setBets, userRef, prevUserIdRef }) {
  const [cloudSynced, setCloudSynced] = useState(false);
  const [syncFailed, setSyncFailed] = useState(false);
  const loadSeqRef = useRef(0);

  const commit = (remoteBets, loadTick) => {
    setBets(remoteBets, { loadTick });
    setCloudSynced(true);
    setSyncFailed(false);
  };

  const retrySync = async () => {
    if (!user || !supabase) return false;
    const syncUserId = user.id;
    // A stale closure (an earlier user) never supersedes the current user's load.
    if (userRef.current?.id !== syncUserId) return false;
    const seq = ++loadSeqRef.current;
    const isCurrent = () => loadSeqRef.current === seq && userRef.current?.id === syncUserId && subjectIs(syncUserId);
    const loadTick = nextLocalTick();
    try {
      const remoteBets = await loadAndMap(syncUserId, isCurrent);
      if (!isCurrent()) return false;
      commit(remoteBets, loadTick);
      return true;
    } catch (err) {
      if (isLoadCancelled(err) || !isCurrent()) return false;
      // No automatic retry follows a manual retry -- terminal for this attempt (CC-IMPL-037).
      captureException(err, { tags: { function: "useCloudSync.retrySync", failureStage: loadFailureReason(err) }, extra: { userId: syncUserId } });
      // This retry may have superseded the effect's first load: end the skeleton either way.
      setSyncFailed(true);
      return false;
    }
  };

  useEffect(() => {
    const seq = ++loadSeqRef.current;
    if (!supabase) return;
    if (!user) {
      setBets([]);
      setCloudSynced(false);
      setSyncFailed(false);
      return;
    }
    if (prevUserIdRef.current !== user.id) {
      prevUserIdRef.current = user.id;
      setCloudSynced(false);
      setSyncFailed(false);
    }
    const userId = user.id;
    let cancelled = false;
    const isCurrent = () => !cancelled && loadSeqRef.current === seq && subjectIs(userId);
    (async () => {
      try {
        let loadTick = nextLocalTick();
        let remoteBets;
        try {
          remoteBets = await loadAndMap(userId, isCurrent);
        } catch (err) {
          if (isLoadCancelled(err) || !isCurrent() || NOT_RETRYABLE.has(loadFailureReason(err))) throw err;
          captureMessage("cloud sync load failed, retrying", { level: "warning", tags: { function: "useCloudSync.effect", stage: loadFailureReason(err) }, extra: { userId } });
          await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
          if (!isCurrent()) return;
          loadTick = nextLocalTick();
          remoteBets = await loadAndMap(userId, isCurrent);
        }
        if (!isCurrent()) return;
        commit(remoteBets, loadTick);
      } catch (err) {
        if (isLoadCancelled(err) || !isCurrent()) return;
        // Terminal for this attempt: the user has Retry; nothing retries automatically (CC-IMPL-037).
        captureException(err, { tags: { function: "useCloudSync.effect", failureStage: loadFailureReason(err) }, extra: { userId } });
        setSyncFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const syncLoading = !!user && !!supabase && !cloudSynced && !syncFailed;
  const syncError = syncFailed && !cloudSynced;

  return { cloudSynced, syncFailed, syncLoading, syncError, retrySync };
}
